/**
 * Constantes del dominio de Pedidos de láminas: permisos por acción (espejo de la
 * API/SSR), etiquetas legibles, catálogo de materiales y diccionario de errores.
 *
 * El gating de la UI usa los MISMOS permisos que exige la API (`application/api/
 * domains/pedidos_lamina.py`) y el SSR (`pedidos_lamina_controller.py`):
 *   view_pedidos_laminas / add_pedidos_laminas / edit_pedidos_laminas (admin) /
 *   send_pedidos_laminas / receive_pedidos_laminas / delete_pedidos_laminas
 *   (admin) / view_historial_pedidos_laminas / retirar_faltante_lamina.
 * El catálogo (categorías, calibres, tubos, medidas) replica el de
 * `PedidoLaminaService` (`pedidos_lamina_service.py`) para que el formulario de
 * alta/edición valide igual que el backend, sin inventar opciones.
 */
import type { PedidoEstado } from "./types";

/** Permiso para ver el tablero (igual que el menú y la API). */
export const VIEW_PERMISSION = "view_pedidos_laminas";
/** Permiso para ver el historial de recibidos. */
export const VIEW_HISTORIAL_PERMISSION = "view_historial_pedidos_laminas";
/** Permiso para crear pedidos (modal "Nuevo pedido" y reporte de faltante). */
export const ADD_PERMISSION = "add_pedidos_laminas";
/** Permiso para editar pedidos pendientes (solo admin, validado en backend). */
export const EDIT_PERMISSION = "edit_pedidos_laminas";
/** Permiso para enviar (transición pendiente → en_camino). */
export const SEND_PERMISSION = "send_pedidos_laminas";
/** Permiso para recibir (transición en_camino → recibido). */
export const RECEIVE_PERMISSION = "receive_pedidos_laminas";
/** Permiso para eliminar pedidos pendientes (solo admin, validado en backend). */
export const DELETE_PERMISSION = "delete_pedidos_laminas";
/** Permiso para retirar avisos de faltante. */
export const RETIRAR_FALTANTE_PERMISSION = "retirar_faltante_lamina";

/** Etiquetas legibles de los estados (UI en español). */
export const ESTADO_LABEL: Record<PedidoEstado, string> = {
  pendiente: "Pendiente",
  en_camino: "En camino",
  recibido: "Recibido",
};

// ── Catálogo de materiales (espejo de pedidos_lamina_service.py) ──────────────

/** Categorías disponibles (orden idéntico al del service). */
export const PEDIDOS_CATEGORIAS = [
  "Lamina galvanizada",
  "Cold Rolled",
  "Lamina HR",
  "Tubos",
  "U de estera",
  "Flejes de jambas",
  "Laminas en acero",
  "Acero 304",
  "Acero 430",
  "Rieles",
] as const;

/** Calibres por categoría (solo las categorías que llevan calibre). */
export const PEDIDOS_CALIBRES: Record<string, string[]> = {
  "Lamina galvanizada": ["30", "28", "26", "24", "22", "20", "18", "16", "14"],
  "Cold Rolled": ["22", "20", "18", "16", "14"],
  "Lamina HR": ["12", "12 ALFAJOR", "1/8", "3/16"],
  "Flejes de jambas": ["22", "20"],
  "Laminas en acero": ["20", "22", "24", "26", "28", "30"],
  "Acero 304": ["24", "22", "20", "18", "16", "14"],
  "Acero 430": ["18", "14", "12"],
};

/** Medidas de tubo. */
export const PEDIDOS_TUBOS = ["7x8", "9x10"] as const;

/**
 * Categorías que llevan medida de lámina (1x2, 4x8, …). Espejo de
 * `PEDIDOS_CON_MEDIDA` del service.
 */
export const PEDIDOS_CON_MEDIDA: ReadonlySet<string> = new Set([
  "Lamina galvanizada",
  "Cold Rolled",
  "Lamina HR",
  "Acero 304",
  "Acero 430",
]);

/** Medidas de lámina ofrecidas en el alta/edición (las que muestra el modal SSR). */
export const PEDIDOS_MEDIDAS = ["1x2", "4x8", "1.22x2.44", "otra"] as const;

/** `true` si la categoría requiere indicar calibre (existe en el mapa de calibres). */
export function categoriaTieneCalibre(categoria: string): boolean {
  return categoria in PEDIDOS_CALIBRES;
}

/** `true` si la categoría requiere medida de lámina. */
export function categoriaTieneMedida(categoria: string): boolean {
  return PEDIDOS_CON_MEDIDA.has(categoria);
}

/** `true` si la categoría es "Tubos" (requiere medida de tubo). */
export function categoriaEsTubo(categoria: string): boolean {
  return categoria === "Tubos";
}

/**
 * Tarjetas de reporte rápido de faltante (sede 41). Replican las 3 tarjetas del
 * SSR ("no hay en bodega"): material fijo + calibres/medidas concretos.
 */
export interface FaltantePreset {
  id: string;
  titulo: string;
  subtitulo: string;
  categoria: string;
  calibres: string[];
  medidas: string[];
}

export const FALTANTE_PRESETS: FaltantePreset[] = [
  {
    id: "galv",
    titulo: "Lámina Galvanizada",
    subtitulo: "Calibres 30 al 14 · 1×2 y 4×8",
    categoria: "Lamina galvanizada",
    calibres: ["30", "28", "26", "24", "22", "20", "18", "16", "14"],
    medidas: ["1x2", "4x8"],
  },
  {
    id: "negra",
    titulo: "Lámina Negra",
    subtitulo: "Calibres 22 al 14 · 1×2 y 1,22×2,44",
    categoria: "Cold Rolled",
    calibres: ["22", "20", "18", "16", "14"],
    medidas: ["1x2", "1.22x2.44"],
  },
  {
    id: "hr",
    titulo: "Lámina H.R.",
    subtitulo: "Cal 12 · 1/8 · 3/16 · Alfajor",
    categoria: "Lamina HR",
    calibres: ["12", "12 ALFAJOR", "1/8", "3/16"],
    medidas: ["1x2", "4x8"],
  },
];

/**
 * Diccionario de mensajes legibles por `error_code` de la API de pedidos
 * (`_SERVICE_ERROR_CODES` en `pedidos_lamina.py`). Si llega un código no listado,
 * la UI cae al `message` del propio `ApiError` (que el backend ya da en español,
 * con el texto exacto del service). Por eso este mapa es mínimo: solo categoriza
 * por tipo de fallo cuando ayuda.
 */
export const PEDIDO_ERROR_MESSAGE: Record<string, string> = {
  forbidden: "No tienes permiso para realizar esta acción.",
  not_found: "El pedido no existe o pertenece a otra sede.",
  validation_error: "Revisa los datos del pedido.",
  internal_error: "Ocurrió un error inesperado. Inténtalo de nuevo.",
};
